import React, { createContext, useContext, useState, useEffect } from 'react';
import useUserInfo from '../hooks/useUserInfo';

export const RestaurantContext = createContext();

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};

export const RestaurantProvider = ({ children }) => {
  const [restaurantId, setRestaurantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const { userInfo, loading: userInfoLoading, refreshUserInfo } = useUserInfo();

  // Initialize restaurant context with data from userInfo (secure backend data)
  useEffect(() => {
    
    if (!userInfoLoading && userInfo) {
      
      // Get current restaurant ID from userInfo (which comes from secure backend)
      let currentRestaurantId = null;
      
      // If user has restaurants, always set one (prioritize matching by restaurantUserId for staff)
      if (userInfo.restaurants && userInfo.restaurants.length > 0) {
        // Try to match by restaurantUserId first (for staff members)
        if (userInfo.restaurantUserId) {
          const matchingRestaurant = userInfo.restaurants.find(r => r.restaurantUserId === userInfo.restaurantUserId);
          if (matchingRestaurant) {
            currentRestaurantId = matchingRestaurant.restaurantId;
           
          }
        }
        
        // Fallback to first restaurant if no match found (handles direct owners and single restaurant cases)
        if (!currentRestaurantId) {
          currentRestaurantId = userInfo.restaurants[0].restaurantId;
    
        }
      } 
      setRestaurantId(currentRestaurantId);
      setLoading(false);
      
      // Dispatch event to notify components that restaurant context is ready
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('restaurantContextReady', { detail: { restaurantId: currentRestaurantId } }));
      }, 100); // Small delay to ensure state is updated
    } else if (!userInfoLoading && !userInfo) {
   
      setRestaurantId(null);
      setLoading(false);
    } 
  }, [userInfo, userInfoLoading]);

  // Listen for login events to refresh user info and restaurant context
  useEffect(() => {
    const handleUserLoggedIn = () => {
      refreshUserInfo();
    };

    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
    };
  }, [refreshUserInfo]);

  // Listen for restaurant switch events to update context
  useEffect(() => {
    const handleRestaurantSwitched = (event) => {
      const { restaurantId: newRestaurantId } = event.detail;
      setRestaurantId(newRestaurantId);
      
      // Dispatch event to notify components that restaurant context was updated
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('restaurantContextUpdated', { detail: { restaurantId: newRestaurantId } }));
      }, 100);
    };

    window.addEventListener('restaurantSwitched', handleRestaurantSwitched);
    
    return () => {
      window.removeEventListener('restaurantSwitched', handleRestaurantSwitched);
    };
  }, []);

  // Simple function to switch restaurant (just updates state)
  const switchRestaurant = (newRestaurantId) => {
    setRestaurantId(newRestaurantId);
  };

  // Get current restaurant ID
  const getCurrentRestaurantId = () => {
    return restaurantId;
  };


  return (
    <RestaurantContext.Provider
      value={{
        restaurantId: getCurrentRestaurantId(),
        switchRestaurant,
        loading: loading || userInfoLoading
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};
